/**
 * AppProvider - Provider Enforcement Wrapper
 * 
 * ARCHITECTURE: Provider Enforcement Pattern
 * Ensures that ALL required providers are present in the correct order.
 * This prevents forgetting critical providers when setting up apps.
 * 
 * Combines CoreProvider + UIProvider with sensible defaults.
 * This is the recommended provider for most OpCentral apps.
 * 
 * CHANGE HISTORY:
 * - 2025-11-25 danielbeer: Update to Provider Enforcement pattern
 * - 2025-11-15 danielbeer: Initial creation - standardized provider orchestration
 */

import React, { ReactNode } from 'react';
import { CoreProvider, CoreProviderProps } from './CoreProvider';
import { UIProvider, UIProviderProps } from './UIProvider';

export interface AppProviderProps extends Omit<CoreProviderProps, 'children'>, Omit<UIProviderProps, 'children'> {
  children: ReactNode;
}

/**
 * AppProvider - Complete application provider bundle
 * 
 * Provides everything an OpCentral app needs:
 * - CoreProvider (Actr + Actr-dependent providers)
 * - UIProvider (UI-related providers)
 * 
 * Usage:
 * ```tsx
 * <AppProvider>
 *   <YourApp />
 * </AppProvider>
 * ```
 * 
 * With configuration:
 * ```tsx
 * <AppProvider
 *   actrConfig={{ namespace: 'myapp' }}
 *   skinnrTheme="dark"
 *   cachrPrefix="myapp_"
 * >
 *   <YourApp />
 * </AppProvider>
 * ```
 */
export function AppProvider({
  children,
  // Core props
  actrConfig,
  enableCommandr = true,
  enableNotifications = true,
  enableTheming = true,
  enableHistory = true,
  skinnrTheme = 'auto',
  skinnrNamespace = 'app',
  notifyrOptions,
  // UI props
  enableInterfacr = true,
  enableProfiles = true,
  enableAggregation = true,
  enableCaching = true,
  enableDialogs = true,
  enableDragDrop = true,
  cachrPrefix = 'app_',
  profylrConfig,
}: AppProviderProps) {
  
  return (
    <CoreProvider
      actrConfig={actrConfig}
      enableCommandr={enableCommandr}
      enableNotifications={enableNotifications}
      enableTheming={enableTheming}
      enableHistory={enableHistory}
      skinnrTheme={skinnrTheme}
      skinnrNamespace={skinnrNamespace}
      notifyrOptions={notifyrOptions}
    >
      <UIProvider
        enableInterfacr={enableInterfacr}
        enableProfiles={enableProfiles}
        enableAggregation={enableAggregation}
        enableCaching={enableCaching}
        enableDialogs={enableDialogs}
        enableDragDrop={enableDragDrop}
        cachrPrefix={cachrPrefix}
        profylrConfig={profylrConfig}
      >
        {children}
      </UIProvider>
    </CoreProvider>
  );
}

export default AppProvider;
